import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FullScreenPreviewService } from './full-screen-preview.service';
import { Subscription } from 'rxjs';
import { Content } from './full-screen-preview.service';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-full-screen-preview',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  template: `
    <div
      class="full-screen-preview"
      [ngClass]="{
        active: openPreview,
        'is-invoice': content.isInvoice,
        'is-image': !content.isHTML && !content.isVideo && !content.isPdf,
        'is-video': content.isVideo,
        'is-pdf': content.isPdf,
      }"
      (click)="closePreview()"
      (mousedown)="
        $event.target === $event.currentTarget ? closePreview() : null
      "
    >
      <div
        class="full-screen-preview-main"
        *ngIf="!destroyContent"
        (click)="
          content.isHTML || content.isVideo || content.isPdf
            ? $event.stopPropagation()
            : null
        "
        (mousedown)="$event.stopPropagation()"
      >
        <div
          #videoContainer
          *ngIf="content.isVideo"
          class="full-screen-preview-video"
          (click)="$event.stopPropagation()"
        >
          <ng-container *ngIf="openPreview && content.isVideo">
            <video
              *ngIf="videoKey"
              #videoEl
              [src]="content.src"
              autoplay
              controls
              playsinline
            ></video>
          </ng-container>
        </div>
        <iframe
          *ngIf="content.isPdf"
          class="full-screen-preview-pdf"
          [src]="safePdfSrc"
          [title]="content.label || 'PDF preview'"
        ></iframe>
        <ng-container *ngIf="!content.isVideo && !content.isPdf">
          <div
            *ngIf="content.isHTML"
            class="full-screen-preview-html"
            [innerHTML]="safeHtml"
          ></div>
          <div *ngIf="!content.isHTML" class="full-screen-preview-image">
            <span *ngIf="content.showLabel" class="full-screen-preview-id">{{
              content.label
            }}</span>
            <div
              class="full-screen-preview-image-container"
              [style.background-image]="'url(' + content.src + ')'"
            ></div>
          </div>
        </ng-container>
      </div>
      <ng-container *ngIf="hasGallery">
        <button
          type="button"
          class="full-screen-preview-nav full-screen-preview-nav--prev"
          aria-label="Previous image"
          (click)="$event.stopPropagation(); step(-1)"
          (mousedown)="$event.stopPropagation()"
        >
          <mat-icon>chevron_left</mat-icon>
        </button>
        <button
          type="button"
          class="full-screen-preview-nav full-screen-preview-nav--next"
          aria-label="Next image"
          (click)="$event.stopPropagation(); step(1)"
          (mousedown)="$event.stopPropagation()"
        >
          <mat-icon>chevron_right</mat-icon>
        </button>
        <span class="full-screen-preview-counter">
          {{ (content.index ?? 0) + 1 }} / {{ content.items!.length }}
        </span>
      </ng-container>
      <div class="full-screen-preview-close" (click)="service.closePreview()">
        <mat-icon> close </mat-icon>
      </div>
    </div>
  `,
  styleUrls: ['./full-screen-preview.component.css'],
})
export class FullScreenPreviewComponent implements OnInit, OnDestroy {
  constructor(
    private sanitizer: DomSanitizer,
    public service: FullScreenPreviewService,
  ) {}
  private subscriptions = new Subscription();

  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;

  content: Content = {
    src: '',
    html: '',
    label: '',
    isHTML: false,
    showLabel: false,
    isVideo: false,
    isPdf: false,
  };

  safeHtml: any;
  safePdfSrc?: SafeResourceUrl;

  openPreview: boolean = false;
  destroyContent: boolean = false;
  activeTimeout: any;

  ngOnInit(): void {
    this.subscriptions.add(
      this.service.getContent().subscribe((content) => {
        if (content?.isVideo && content.src !== this.content?.src) {
          this.videoKey++;
        }

        this.destroyContent = false;
        this.content = content;

        if (this.content.isHTML) {
          this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(
            this.content.html,
          );
        }

        this.safePdfSrc = this.content.isPdf
          ? this.sanitizer.bypassSecurityTrustResourceUrl(this.content.src)
          : undefined;
      }),
    );
    this.subscriptions.add(
      this.service.getIsPreviewOpen().subscribe((isPreviewOpen) => {
        this.openPreview = isPreviewOpen;

        this.activeTimeout = setTimeout(() => {
          if (!this.openPreview) {
            this.destroyContent = true;
          }
        }, 300);

        if (isPreviewOpen) {
          document.body.classList.add('preview-open');
        } else {
          document.body.classList.remove('preview-open');
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    clearTimeout(this.activeTimeout);
  }

  videoKey = 0;

  private resetVideoElement(): void {
    const video = this.videoEl?.nativeElement;
    if (!video) return;

    try {
      video.pause();
      video.removeAttribute('src');
      video.load();
    } catch {}
  }

  get hasGallery(): boolean {
    return (
      !this.content.isHTML &&
      !this.content.isVideo &&
      !this.content.isPdf &&
      (this.content.items?.length ?? 0) > 1
    );
  }

  /** Moves through the gallery, wrapping round at either end. */
  step(direction: 1 | -1): void {
    const items = this.content.items;
    if (!this.hasGallery || !items) return;
    const index =
      ((this.content.index ?? 0) + direction + items.length) % items.length;
    this.service.setContent({
      ...this.content,
      src: items[index].src,
      label: items[index].label,
      index,
    });
  }

  closePreview() {
    this.resetVideoElement();
    this.videoKey++;

    this.service.closePreview();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.openPreview) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      const target = event.target as HTMLElement;
      if (target && typeof target.blur === 'function') {
        target.blur();
      }
      this.closePreview();
      return;
    }

    if (
      this.hasGallery &&
      (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.step(event.key === 'ArrowLeft' ? -1 : 1);
    }
  }
}
